import type { Mint } from "@solana/spl-token0.3.8";
import type { AccountInfo, Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import type { ParsedAccount, ParsedAccountBase } from "@stepfinance/common";
import { NATIVE_SOL, SYSTEM_PROGRAM } from "@stepfinance/common";
import type { TokenAccount } from "@stepfinance/tokens";
import { MintParser, TokenAccountParser } from "@stepfinance/tokens";

import { cache } from "../cache";

const wrapNativeAccount = (pubkey: PublicKey, account?: AccountInfo<Buffer>): TokenAccount | undefined => {
  if (!account) {
    return undefined;
  }

  return {
    pubkey: pubkey,
    account,
    info: {
      address: pubkey,
      mint: NATIVE_SOL,
      owner: pubkey,
      amount: BigInt(account.lamports),
      delegate: null,
      delegatedAmount: BigInt(0),
      isInitialized: true,
      isFrozen: false,
      isNative: true,
      rentExemptReserve: null,
      closeAuthority: null,
      tlvData: Buffer.alloc(0),
    },
  };
};

export const wrapNativeMintInfo = (): Mint => ({
  address: NATIVE_SOL,
  mintAuthority: null,
  supply: BigInt(0),
  decimals: 9,
  isInitialized: true,
  freezeAuthority: null,
  tlvData: Buffer.alloc(0),
});

const updateCache = (
  genericCache: Map<string, ParsedAccountBase>,
  mintCache: Map<string, ParsedAccount<Mint>>,
  publicKey: PublicKey,
  account: AccountInfo<Buffer>,
) => {
  const wrapped = wrapNativeAccount(publicKey, account);
  if (wrapped !== undefined) {
    const id = publicKey.toBase58();
    cache.registerParser(id, TokenAccountParser);
    genericCache.set(id, wrapped);
    cache.emitter.raiseCacheUpdated(id, false, TokenAccountParser);

    const wrappedMintInfo = wrapNativeMintInfo();
    const parsedWrappedMintInfo: ParsedAccount<Mint> = {
      pubkey: new PublicKey(wrappedMintInfo.address),
      // throw in some dummy data to appease the type gods
      account: {
        executable: false,
        owner: SYSTEM_PROGRAM,
        lamports: 0,
        data: Buffer.alloc(0),
      },
      info: wrappedMintInfo,
    };
    cache.registerParser(NATIVE_SOL.toBase58(), MintParser);
    mintCache.set(NATIVE_SOL.toBase58(), parsedWrappedMintInfo);
    cache.emitter.raiseCacheUpdated(NATIVE_SOL.toBase58(), false, MintParser);
  }
};

export const updateNativeAccount = async (
  connection: Connection,
  genericCache: Map<string, ParsedAccountBase>,
  mintCache: Map<string, ParsedAccount<Mint>>,
  publicKey: PublicKey,
): Promise<AccountInfo<Buffer> | undefined> => {
  const account = await connection.getAccountInfo(publicKey);
  if (account) {
    updateCache(genericCache, mintCache, publicKey, account);
    return account;
  }

  return undefined;
};
